import { memo } from 'react'
import { AppsLink } from '@wellesley/router'
import { UserAvatar } from '@wellesley/user'
import { cn } from '@wellesley/ui'
import { GroupMember, Relationship, User, UserFull } from '@wellesley/openapi'
import { getUserImage } from 'helpers/getUserImage'
import { getUserNameWithDomain } from 'helpers/getUserNameWithDomain'
import FollowWithActions, {
  FollowWithActionsProps,
} from '../../FollowControl/FollowWithActions'

type RowItemProps = {
  item: User | GroupMember
  relationship: Relationship
  isRelationshipLoading: boolean
  className?: string
  onClose: () => void
} & Pick<FollowWithActionsProps, 'onFollow' | 'onUnfollow'>

export const checkIfUser = (item: User | GroupMember): item is User =>
  'username' in item

export const getRowId = (item: User | GroupMember) =>
  checkIfUser(item) ? item.id : item.memberId

// Note: id is hard coded for remote users
export const getRowUniqueKey = (item: User | GroupMember) =>
  checkIfUser(item) ? item.uri : item.memberId

const RowItem = memo<RowItemProps>(
  ({
    item,
    relationship,
    isRelationshipLoading,
    onClose,
    className,
    ...handlers
  }) => {
    const isUser = checkIfUser(item)
    const displayName = isUser ? item.displayName : item.userDisplayName

    const username = getUserNameWithDomain({
      username: isUser ? item.username : item.userName,
      domain: isUser ? item.domain : item.userDomain || '',
    } as User)

    return (
      <div
        className={cn(
          'flex min-w-0 max-w-full items-center justify-between gap-2.5',
          className,
        )}
      >
        <AppsLink
          to="/@/:username"
          onClick={onClose}
          className="min-w-0"
          props={{ username }}
        >
          <div className="flex">
            <UserAvatar
              size={40}
              name={displayName}
              imgUrl={getUserImage({
                user: item as UserFull,
                type: 'avatar',
              })}
            />
            <div className="ml-xxs min-w-0 text-gray-700">
              <div className="cursor-pointer">
                <div className="truncate text-base font-semibold">
                  {displayName}
                </div>
                <div className="truncate text-base font-medium">{username}</div>
              </div>
            </div>
          </div>
        </AppsLink>
        <FollowWithActions
          user={item as UserFull}
          size="s"
          relationship={relationship}
          isRelationshipPending={isRelationshipLoading}
          {...handlers}
        />
      </div>
    )
  },
)

RowItem.displayName = 'UserRow'

export default RowItem
